import React, { useState, useEffect } from 'react';
import {
  parseCsvToDataFrame,
  parseXlsxToDataFrame,
  getTopGenesPerGroup,
  getTopGenesHeatmap,
  getTopGenesForGroupHeatmap,
  DataFrame
} from '../components/analysis/dataUtils';
import TopGenesTable from '../components/analysis/TopGenesTable';
import GroupHeatmap from '../components/analysis/GroupHeatmap';
import DataSummary from '../components/analysis/DataSummary';
import FileChooser from '../components/analysis/FileChooser';
import UmapChart from '../components/analysis/UmapChart';
import UpSetPlot from '../components/analysis/UpSetPlot';
import SheetSelectModal from '../components/analysis/SheetSelectModal';

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  gene: string;
  group: string;
  value: number | null;
}

const GeneExpressionAnalysis: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [df, setDf] = useState<DataFrame | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [topGenes, setTopGenes] = useState<Record<string, string[]> | null>(null);
  const [geneCounts, setGeneCounts] = useState<any[]>([]);
  const [heatmapGroups, setHeatmapGroups] = useState<string[]>([]);
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, gene: '', group: '', value: null });
  // For group heatmap
  const [groupHeatmapGroups, setGroupHeatmapGroups] = useState<string[]>([]);
  const [groupHeatmapGenes, setGroupHeatmapGenes] = useState<string[]>([]);
  const [groupHeatmapMatrix, setGroupHeatmapMatrix] = useState<any>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [pendingXlsx, setPendingXlsx] = useState<{ data: ArrayBuffer; sheets: string[] } | null>(null);
  const [sheetToParse, setSheetToParse] = useState<string | null>(null);

  // Handle file upload and parse
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setFile(f);
    setSummary(null);
    setError('');
    setPendingXlsx(null);
    setSheetToParse(null);
    if (f) {
      const ext = f.name.split('.').pop()?.toLowerCase();
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          let parsedDf: DataFrame;
          if (ext === 'csv') {
            const text = evt.target && typeof evt.target.result === 'string' ? evt.target.result : '';
            parsedDf = parseCsvToDataFrame(text);
            setDf(parsedDf);
            setSummary({
              rows: parsedDf.index.length,
              columns: parsedDf.columns.length,
              column_names: parsedDf.columns,
              head: parsedDf.index.slice(0, 5).map(gene => ({ gene, ...parsedDf.data[gene] }))
            });
          } else if (ext === 'xlsx') {
            const data = evt.target?.result as ArrayBuffer;
            const XLSX = await import('xlsx');
            const workbook = XLSX.read(data, { type: 'array' });
            if (workbook.SheetNames.length > 1) {
              setPendingXlsx({ data, sheets: workbook.SheetNames });
            } else {
              parsedDf = parseXlsxToDataFrame(data, workbook.SheetNames[0]);
              setDf(parsedDf);
              setSummary({
                rows: parsedDf.index.length,
                columns: parsedDf.columns.length,
                column_names: parsedDf.columns,
                head: parsedDf.index.slice(0, 5).map(gene => ({ gene, ...parsedDf.data[gene] }))
              });
            }
          } else {
            throw new Error('Unsupported file type.');
          }
        } catch (err: any) {
          setError('Failed to parse file.');
        }
      };
      if (ext === 'csv') {
        reader.readAsText(f);
      } else if (ext === 'xlsx') {
        reader.readAsArrayBuffer(f);
      } else {
        setError('Unsupported file type.');
      }
    } else {
      setDf(null);
    }
  };

  // Handle sheet selection for XLSX
  useEffect(() => {
    if (pendingXlsx && sheetToParse) {
      const parsedDf: DataFrame = parseXlsxToDataFrame(pendingXlsx.data, sheetToParse);
      setDf(parsedDf);
      setSummary({
        rows: parsedDf.index.length,
        columns: parsedDf.columns.length,
        column_names: parsedDf.columns,
        head: parsedDf.index.slice(0, 5).map(gene => ({ gene, ...parsedDf.data[gene] }))
      });
      setPendingXlsx(null);
      setSheetToParse(null);
    }
  }, [pendingXlsx, sheetToParse]);

  // Compute all analysis when df changes
  useEffect(() => {
    if (!df) {
      setTopGenes(null);
      setGeneCounts([]);
      setHeatmapGroups([]);
      setSelectedGroup('');
      setGroupHeatmapGroups([]);
      setGroupHeatmapGenes([]);
      setGroupHeatmapMatrix(null);
      return;
    }
    // Top genes per group and gene counts
    const { topGenesPerGroup, geneCounts } = getTopGenesPerGroup(df, 50);
    setTopGenes(topGenesPerGroup);
    setGeneCounts(geneCounts);
    // Prepare heatmap data: top 50 most frequent genes
    const top50 = (geneCounts || []).slice(0, 50).map(row => row.gene);
    const groups = df.columns;
    setHeatmapGroups(groups);
    setSelectedGroup(groups[0] || '');
  }, [df]);

  // Compute union of all top 50s per group (same as TopGenesTable)
  let umapGenes: string[] = [];
  if (topGenes) {
    const groups = Object.keys(topGenes);
    const seen = new Set<string>();
    for (const group of groups) {
      for (const gene of topGenes[group]) {
        if (!seen.has(gene)) {
          umapGenes.push(gene);
          seen.add(gene);
        }
      }
    }
    // Sort by overlap count (geneCounts), descending
    const geneCountMap = Object.fromEntries((geneCounts || []).map(row => [row.gene, row.count]));
    umapGenes.sort((a, b) => (geneCountMap[b] || 0) - (geneCountMap[a] || 0));
    // No slice(0, 100) -- include all top genes from TopGenesTable
  }

  // Group heatmap: update when selectedGroup changes
  useEffect(() => {
    if (!df || !selectedGroup) {
      setGroupHeatmapGroups([]);
      setGroupHeatmapGenes([]);
      setGroupHeatmapMatrix(null);
      return;
    }
    const { genes, groups, matrix } = getTopGenesForGroupHeatmap(df, selectedGroup, 50);
    setGroupHeatmapGroups(groups);
    setGroupHeatmapGenes(genes);
    setGroupHeatmapMatrix(matrix);
  }, [df, selectedGroup]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ textAlign: 'center', color: '#333' }}>Gene Expression Analysis</h1>
      <FileChooser onFileChange={handleFileChange} />
      {error && <div style={{ color: 'red', marginBottom: 10 }}>{error}</div>}
      {summary && (
        <DataSummary summary={summary} />
      )}
      {df && (
        <div>
          {/* Top Genes Analysis Table */}
          {topGenes && df && (
            <TopGenesTable topGenes={topGenes} df={df} />
          )}
          {/* UpSet Plot */}
          {topGenes && (
            <UpSetPlot sets={topGenes} />
          )}
          {/* Group Heatmap */}
          {groupHeatmapGenes.length && groupHeatmapGroups.length && groupHeatmapMatrix && (
            <GroupHeatmap
              genes={groupHeatmapGenes}
              groups={groupHeatmapGroups}
              values={groupHeatmapMatrix}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
              heatmapGroups={heatmapGroups}
              tooltip={tooltip}
              setTooltip={setTooltip}
            />
          )}
          {/* UMAP Chart */}
          <UmapChart 
            df={df} 
            genes={umapGenes}
            geneGroupCounts={Object.fromEntries((geneCounts || []).map(row => [row.gene, row.count]))}
          />
        </div>
      )}
      <div style={{ marginTop: 40, fontSize: 12, color: '#666', textAlign: 'center' }}>
        <p>Uploaded CSV File: {file ? file.name : 'None'}</p>
        <p>Note: This tool is for visualizing gene expression data. Ensure your data is properly formatted.</p>
      </div>
      {pendingXlsx && (
        <SheetSelectModal
          sheets={pendingXlsx.sheets}
          onSelect={sheet => setSheetToParse(sheet)}
          onCancel={() => setPendingXlsx(null)}
        />
      )}
    </div>
  );
};

export default GeneExpressionAnalysis;
